import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, Box, MapPin, User, 
  FileText, Calendar, Trash2, X, Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import { buildSalePaymentTerms } from '../utils/salesReceivables';
import { annulSaleWithReversal } from '../utils/saleAnnulment';

function VentaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [venta, setVenta] = useState(null);
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [showConfirmAnular, setShowConfirmAnular] = useState(false);

  const paymentTerms = buildSalePaymentTerms({
    sale: venta,
    client: venta?.clients,
  });

  useEffect(() => {
    if (profile?.organization_id) fetchDetalleYConfig();
  }, [id, profile]);

  const toDateOnly = (value) => {
    if (!value) return '';
    const raw = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const [datePart] = raw.split('T');
    return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : raw;
  };

  const saleDateLabel = venta?.fecha_venta || toDateOnly(venta?.created_at);
  const billingDateLabel = venta?.fecha_facturacion || toDateOnly(venta?.invoice_sent_at) || '';

  async function fetchDetalleYConfig() {
    try {
      setLoading(true);
      const [saleRes, itemsRes, configRes] = await Promise.all([
        supabase.from('sales').select('*, clients(*), sitios(*)').eq('id', id).single(),
        supabase.from('sale_items').select('*, products!vinc_prod(name, image_url)').eq('sale_id', id),
        supabase.from('printed_reports_config').select('*').eq('organization_id', profile.organization_id).single()
      ]);

      if (saleRes.error) throw saleRes.error;
      setVenta(saleRes.data);
      setItems(itemsRes.data || []);
      setConfig(configRes.data);
    } catch (error) {
      toast.error("Venta no encontrada");
      navigate('/sistema/ventas-historial');
    } finally {
      setLoading(false);
    }
  }

  const handleAnular = async () => {
    // Mismo guard que VentasHistorial: una factura emitida en ARCA no se
    // anula, requiere nota de crédito.
    if (venta?.invoice_status === 'facturado') {
      toast.error("No se puede anular una factura emitida.");
      setShowConfirmAnular(false);
      return;
    }
    try {
      // Reversión completa y compartida con VentasHistorial: estado, cuenta
      // corriente, receivable, stock (con kardex y sin duplicar combos),
      // libro diario y banco. Antes esta pantalla reintegraba stock "a mano"
      // (duplicando la fila resumen de los combos y sin registrar kardex) y
      // no tocaba banco ni libro diario.
      const result = await annulSaleWithReversal({
        saleId: id,
        organizationId: profile.organization_id,
        movementType: 'Venta POS',
      });

      if (result?.skipped && result.reason === 'already_annulled') {
        toast.success("La venta ya estaba anulada");
      } else if (result?.reason === 'revision_sin_reversion') {
        toast.success("Venta en revisión anulada (no había stock ni caja que revertir)");
      } else {
        toast.success("Venta anulada y stock reintegrado");
      }

      // Si la devolución en Mercado Pago falló, la plata sigue cobrada.
      if (result?.refund?.attempted && result.refund.refunded === false) {
        toast.error(
          `La venta se anuló, pero NO se pudo devolver el cobro en Mercado Pago: ${result.refund.error || 'error desconocido'}. Hacelo desde el panel de MP.`,
          { duration: 14000 },
        );
      }
      setShowConfirmAnular(false);
      fetchDetalleYConfig();
    } catch (error) {
      console.error(error);
      toast.error("Error al procesar la anulación");
    }
  };

  if (loading) return (
    <div className="h-screen flex items-center justify-center bg-[#F2F9FF]">
      <div className="text-center font-black uppercase text-slate-400 text-[10px] tracking-[0.3em] animate-pulse">
        Sincronizando...
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F2F9FF] p-4 md:p-8 font-sans text-slate-700 animate-in fade-in duration-500">
      
      {/* ACCIONES SUPERIORES */}
      <div className="max-w-5xl mx-auto flex justify-between items-center mb-6 print:hidden">
        <button 
          onClick={() => navigate('/sistema/ventas-historial')} 
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-600 font-black uppercase text-[10px] tracking-widest hover:bg-slate-50 transition-all shadow-sm"
        >
          <ArrowLeft size={14} /> Volver
        </button>

        <div className="flex items-center gap-2">
          {showConfirmAnular ? (
            <div className="flex items-center gap-2 bg-red-50 p-1 px-3 rounded-lg border border-red-100 animate-in slide-in-from-right-2">
              <p className="text-[9px] font-black text-red-600 uppercase tracking-tighter mr-2">¿Confirmar anulación y reintegro?</p>
              <button onClick={handleAnular} className="p-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 shadow-sm"><Check size={14} strokeWidth={3} /></button>
              <button onClick={() => setShowConfirmAnular(false)} className="p-1.5 bg-slate-200 text-slate-600 rounded-md hover:bg-slate-300 shadow-sm"><X size={14} strokeWidth={3} /></button>
            </div>
          ) : (
            <button 
              onClick={() => setShowConfirmAnular(true)}
              disabled={!venta.active}
              className="flex items-center gap-2 px-4 py-2 bg-[#D9534F] text-white rounded-lg font-black uppercase text-[10px] tracking-widest shadow-md hover:bg-[#c9302c] disabled:opacity-50 disabled:grayscale transition-all"
            >
              <Trash2 size={14}/> Anular Comprobante
            </button>
          )}
        </div>
      </div>

      {/* COMPROBANTE TÉCNICO COMPACTO */}
      <div className="bg-white max-w-5xl mx-auto rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        
        {/* CABECERA */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-start">
          <div>
            <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-1">Detalle Venta</h1>
            <div className="flex gap-4 text-[10px] font-bold text-slate-400 uppercase tracking-tight">
              <p>Compact: ID: <span className="text-slate-600">{venta.id.slice(0, 8)}</span></p>
              <p>Fecha venta: <span className="text-slate-600">{saleDateLabel}</span></p>
              {billingDateLabel && <p>Fecha facturacion: <span className="text-slate-600">{billingDateLabel}</span></p>}
            </div>
          </div>
          <span className={`px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
            venta.active ? 'bg-[#5CB85C] text-white' : 'bg-slate-400 text-white'
          }`}>
            {venta.active ? (venta.invoice_status || 'ACTIVO') : 'ANULADO'}
          </span>
        </div>

        {/* INFO ENTIDADES */}
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-slate-100 rounded-xl p-3.5 flex gap-3 items-center bg-slate-50/20">
            <User size={16} className="text-slate-300" />
            <div className="flex-1">
              <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Datos del Cliente</h4>
              <p className="text-xs font-black uppercase text-slate-700 leading-none">{venta.clients?.full_name || 'Consumidor Final'}</p>
            </div>
          </div>
          
          <div className="border border-slate-100 rounded-xl p-3.5 flex gap-3 items-center bg-slate-50/20 relative">
            <MapPin size={16} className="text-slate-300" />
            <div className="flex-1">
              <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Origen Operativo</h4>
              <p className="text-xs font-black uppercase text-slate-700 leading-none">{venta.sitios?.nombre || 'General'}</p>
            </div>
            <span className="bg-slate-400 text-white px-2 py-0.5 rounded text-[8px] font-black uppercase">
              {venta.payment_method || 'Pago'}
            </span>
          </div>
        </div>

        <div className="px-5 pb-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-slate-100 rounded-xl p-3.5 bg-slate-50/20">
            <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">
              Condicion de Pago
            </h4>
            <p className="text-xs font-black uppercase text-slate-700 leading-none">
              {paymentTerms.label || 'Contado'}
            </p>
          </div>

          <div className="border border-slate-100 rounded-xl p-3.5 bg-slate-50/20">
            <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">
              Vencimiento
            </h4>
            <p className="text-xs font-black uppercase text-slate-700 leading-none">
              {paymentTerms.dueDate
                ? new Date(`${paymentTerms.dueDate}T12:00:00`).toLocaleDateString('es-AR')
                : 'Sin vencimiento'}
            </p>
          </div>
        </div>

        {/* TABLA DE PRODUCTOS */}
        <div className="px-5 pb-5">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-y border-slate-100 bg-slate-50/50 text-[9px] font-black uppercase text-slate-400 tracking-wider">
                <th className="py-2 px-2">Producto</th>
                <th className="py-2 px-2 text-center w-24">Cantidad</th>
                <th className="py-2 px-2 text-right w-40">Precio unidad</th>
                <th className="py-2 px-2 text-right w-40">Subtotal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {items.map((item, idx) => (
                <tr key={idx} className="text-[11px] font-bold text-slate-600">
                  <td className="py-3 px-2 uppercase tracking-tight">{item.products?.name}</td>
                  <td className="py-3 px-2 text-center">{item.quantity}</td>
                  <td className="py-3 px-2 text-right">$ {item.unit_price.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                  <td className="py-3 px-2 text-right font-black text-slate-800">$ {(item.quantity * item.unit_price).toLocaleString('es-AR', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* FOOTER TOTALIZADOR */}
        <div className="bg-[#1B252E] text-white p-4 flex justify-between items-center">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tight italic leading-none">
            Comprobante de operación interna.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Total:</span>
            <span className="text-2xl font-black tracking-tighter leading-none text-white">
              $ {venta.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VentaDetalle;
